import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Empty,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import {
  ReloadOutlined,
  DatabaseOutlined,
  DownloadOutlined,
} from "@ant-design/icons";

const { Text } = Typography;

function getTelegramInitData() {
  try {
    return window?.Telegram?.WebApp?.initData || "";
  } catch {
    return "";
  }
}

function getTelegramUserId() {
  try {
    return Number(window?.Telegram?.WebApp?.initDataUnsafe?.user?.id || 0) || 0;
  } catch {
    return 0;
  }
}

async function apiFetch(path, { method = "GET", body, query } = {}) {
  const initData = getTelegramInitData();
  const qs =
    query && Object.keys(query).length
      ? "?" +
        Object.entries(query)
          .filter(([, v]) => v !== undefined && v !== null && String(v) !== "")
          .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
          .join("&")
      : "";

  const res = await fetch(`/api${path}${qs}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(initData ? { "x-telegram-init-data": initData } : {}),
    },
    credentials: "include",
    body: body ? JSON.stringify(body) : undefined,
  });

  let data = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }

  if (!res.ok) {
    const err = new Error(data?.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }

  return data;
}

function formatCell(value, key) {
  if (value == null) return "—";

  const k = String(key || "").toLowerCase();

  if (
    k.endsWith("_ts") ||
    k.includes("time") ||
    k === "created_at" ||
    k === "updated_at"
  ) {
    const n = Number(value);
    if (Number.isFinite(n) && n > 0) {
      const ms = n < 10_000_000_000 ? n * 1000 : n;
      const d = new Date(ms);
      if (!Number.isNaN(d.getTime())) return d.toLocaleString();
    }
  }

  if (typeof value === "boolean") return value ? "true" : "false";
  if (typeof value === "object") return JSON.stringify(value);

  return String(value);
}

function toCsv(rows, columns) {
  const esc = (v) => {
    const s = String(v ?? "");
    if (/[",\n;]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const head = columns.map(esc).join(";");
  const body = rows.map((row) => columns.map((c) => esc(row?.[c])).join(";")).join("\n");
  return `${head}\n${body}`;
}

async function downloadTextFile(filename, content, mimeType) {
  try {
    const blob = new Blob([content], { type: `${mimeType};charset=utf-8` });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    setTimeout(() => URL.revokeObjectURL(url), 1500);
    return true;
  } catch (_) {
    return false;
  }
}

export default function Database() {
  const [tables, setTables] = useState([]);
  const [tableName, setTableName] = useState("");
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [total, setTotal] = useState(0);

  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);

  const [sort, setSort] = useState("");
  const [order, setOrder] = useState("desc");

  const [loadingTables, setLoadingTables] = useState(false);
  const [loadingRows, setLoadingRows] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 250);
    return () => clearTimeout(t);
  }, [searchInput]);

  async function loadTables() {
    setLoadingTables(true);
    setError("");
    try {
      const data = await apiFetch("/db/tables");
      const arr = Array.isArray(data?.tables) ? data.tables : [];
      setTables(arr);
      if (!tableName && arr.length) setTableName(arr[0]);
    } catch (e) {
      setError(e?.message || "Ошибка загрузки списка таблиц");
    } finally {
      setLoadingTables(false);
    }
  }

  async function loadRows() {
    if (!tableName) return;
    setLoadingRows(true);
    setError("");
    try {
      const data = await apiFetch("/db/table", {
        query: {
          name: tableName,
          limit,
          offset,
          search: search || undefined,
          sort: sort || undefined,
          order: order || undefined,
        },
      });

      const arr = Array.isArray(data?.rows) ? data.rows : [];
      const cols = Array.isArray(data?.columns) ? data.columns : [];

      setRows(arr);
      setColumns(cols);
      setTotal(Number(data?.total || 0));
    } catch (e) {
      setRows([]);
      setColumns([]);
      setTotal(0);
      setError(e?.message || "Ошибка загрузки таблицы");
    } finally {
      setLoadingRows(false);
    }
  }

  async function loadAllRowsForExport() {
    if (!tableName) return { rows: [], columns: [] };

    const data = await apiFetch("/db/table", {
      query: {
        name: tableName,
        limit: 100000,
        offset: 0,
        search: search || undefined,
        sort: sort || undefined,
        order: order || undefined,
      },
    });

    return {
      rows: Array.isArray(data?.rows) ? data.rows : [],
      columns: Array.isArray(data?.columns) ? data.columns : [],
    };
  }

  async function exportJson() {
    try {
      if (!tableName) return;

      const tgUserId = getTelegramUserId();
      if (!tgUserId) {
        message.error("Не удалось определить Telegram user id");
        return;
      }

      message.loading({ content: "Отправляем JSON в Telegram...", key: "db-send-json" });

      const data = await apiFetch("/db/export/send", {
        method: "POST",
        body: {
          name: tableName,
          format: "json",
          tg_user_id: tgUserId,
          ...(search ? { search } : {}),
          ...(sort ? { sort } : {}),
          ...(order ? { order } : {}),
        },
      });

      if (!data?.ok) throw new Error(data?.error || "send_failed");

      message.success({
        content: "JSON отправлен вам в Telegram",
        key: "db-send-json",
      });
    } catch (e) {
      message.error({
        content: "Не удалось отправить JSON в Telegram",
        key: "db-send-json",
      });
    }
  }

  async function exportCsv() {
    try {
      if (!tableName) return;

      const tgUserId = getTelegramUserId();
      if (!tgUserId) {
        message.error("Не удалось определить Telegram user id");
        return;
      }

      message.loading({ content: "Отправляем CSV в Telegram...", key: "db-send-csv" });

      const data = await apiFetch("/db/export/send", {
        method: "POST",
        body: {
          name: tableName,
          format: "csv",
          tg_user_id: tgUserId,
          ...(search ? { search } : {}),
          ...(sort ? { sort } : {}),
          ...(order ? { order } : {}),
        },
      });

      if (!data?.ok) throw new Error(data?.error || "send_failed");

      message.success({
        content: "CSV отправлен вам в Telegram",
        key: "db-send-csv",
      });
    } catch (e) {
      message.error({
        content: "Не удалось отправить CSV в Telegram",
        key: "db-send-csv",
      });
    }
  }

  useEffect(() => {
    loadTables();
  }, []);

  useEffect(() => {
    setOffset(0);
  }, [tableName, search, limit]);

  useEffect(() => {
    loadRows();
  }, [tableName, limit, offset, search, sort, order]);

  const tableOptions = useMemo(
    () => tables.map((t) => ({ value: t, label: t })),
    [tables]
  );

  const tableColumns = useMemo(() => {
    return columns.map((col) => ({
      title: (
        <Space size={6}>
          <span>{col}</span>
          {sort === col ? <Tag color="blue">{order}</Tag> : null}
        </Space>
      ),
      dataIndex: col,
      key: col,
      width: 180,
      ellipsis: true,
      render: (v) => (
        <span title={typeof v === "object" ? JSON.stringify(v) : String(v ?? "")}>
          {formatCell(v, col)}
        </span>
      ),
      sorter: false,
    }));
  }, [columns, sort, order]);

  return (
    <div style={{ padding: 16 }}>
      <Card
        style={{ borderRadius: 16 }}
        bodyStyle={{ padding: 16 }}
      >
        <Space direction="vertical" size={14} style={{ width: "100%" }}>
          <Space align="center" wrap>
            <DatabaseOutlined />
            <Typography.Title level={3} style={{ margin: 0 }}>
              База данных
            </Typography.Title>
            <Tag color="blue">read-only</Tag>
          </Space>

          <Typography.Text type="secondary">
            Просмотр таблиц БД без прямого SQL. Только безопасный whitelist.
          </Typography.Text>

          <Space wrap style={{ width: "100%" }}>
            <div style={{ minWidth: 220 }}>
              <Text type="secondary">Таблица</Text>
              <Select
                style={{ width: "100%", marginTop: 6 }}
                value={tableName || undefined}
                options={tableOptions}
                loading={loadingTables}
                onChange={(v) => setTableName(v)}
                placeholder="Выберите таблицу"
              />
            </div>

            <div style={{ minWidth: 240 }}>
              <Text type="secondary">Поиск</Text>
              <Input
                style={{ width: "100%", marginTop: 6 }}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="по тексту / id / chat_id"
                allowClear
              />
            </div>

            <div style={{ minWidth: 120 }}>
              <Text type="secondary">Лимит</Text>
              <Select
                style={{ width: "100%", marginTop: 6 }}
                value={limit}
                onChange={setLimit}
                options={[
                  { value: 25, label: "25" },
                  { value: 50, label: "50" },
                  { value: 100, label: "100" },
                  { value: 200, label: "200" },
                ]}
              />
            </div>

            <div style={{ minWidth: 180 }}>
              <Text type="secondary">Сортировка</Text>
              <Select
                style={{ width: "100%", marginTop: 6 }}
                value={sort || undefined}
                onChange={(v) => setSort(v || "")}
                placeholder="без сортировки"
                allowClear
                options={columns.map((c) => ({ value: c, label: c }))}
              />
            </div>

            <div style={{ minWidth: 120 }}>
              <Text type="secondary">Порядок</Text>
              <Select
                style={{ width: "100%", marginTop: 6 }}
                value={order}
                onChange={setOrder}
                options={[
                  { value: "asc", label: "asc" },
                  { value: "desc", label: "desc" },
                ]}
              />
            </div>
          </Space>

          <Space wrap>
            <Button icon={<ReloadOutlined />} onClick={loadRows} loading={loadingRows}>
              Обновить
            </Button>
            <Button icon={<DownloadOutlined />} onClick={exportCsv} disabled={!rows.length}>
              CSV → Telegram
            </Button>
            <Button icon={<DownloadOutlined />} onClick={exportJson} disabled={!rows.length}>
              JSON → Telegram
            </Button>
            <Tag color="default">Строк: {total}</Tag>
            <Tag color="default">Показано: {rows.length}</Tag>
          </Space>

          {error ? (
            <Alert
              type="error"
              showIcon
              message="Ошибка"
              description={error}
            />
          ) : null}

          <Table
            rowKey={(r, i) => String(r?.id ?? `${tableName}:${offset + i}`)}
            loading={loadingRows}
            columns={tableColumns}
            dataSource={rows}
            pagination={{
              current: Math.floor(offset / limit) + 1,
              pageSize: limit,
              total,
              showSizeChanger: false,
              onChange: (page) => setOffset((page - 1) * limit),
            }}
            scroll={{ x: Math.max(columns.length * 180, 900) }}
            sticky
            locale={{
              emptyText: (
                <Empty description={tableName ? "Нет строк" : "Выберите таблицу"} />
              ),
            }}
          />
        </Space>
      </Card>
    </div>
  );
}